import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth";
import { UserManagementPage } from "./pages/UserManagementPage";
import { RoleManagementPage } from "./pages/RoleManagementPage";
import { ChecklistTemplatePage } from "./pages/ChecklistTemplatePage";
import { ComplianceCalendarPage } from "./pages/compliance/ComplianceCalendarPage";
import { DocumentChangeReportsPage } from "./pages/DocumentChangeReportsPage";
import {
  chartOfAccountsSyncHadBillInvoiceRuleFailure,
  GL_SYNC_BILL_INVOICE_RULES,
  GL_SYNC_BILL_INVOICE_RULES_FAILED_MESSAGE,
} from "../../gls/constants/glSyncBillInvoiceRules";
import { AdminGlMappingsTable } from "../../gls/presentation/components";
import { TdsSyncCard } from "./components/TdsSyncCard";
import { Badge } from "../../../ui/components/Badge";
import { Button } from "../../../ui/components/Button";
import { Tabs } from "../../../ui/components/Tabs";
import { DefaultPageLayout } from "../../../ui/layouts/DefaultPageLayout";
import {
  FeatherLogOut,
  FeatherUsers,
  FeatherRefreshCw,
  FeatherShield,
  FeatherCheckSquare,
  FeatherFileText,
  FeatherBriefcase,
  FeatherArrowRight,
  FeatherBarChart2,
  FeatherAlertTriangle,
} from "@subframe/core";
import {
  syncCurrencies,
  syncTaxes,
  syncChartOfAccounts,
  SyncResult,
  ChartOfAccountsSyncResult,
} from "../../../services/api";
import { getStoredAccountingBackend } from "../../../config/accounting";
import { SyncModuleCard } from "./components/SyncModuleCard";
import { tallySyncApi } from "../../../services/tallySyncApi";
import { TallySyncModule } from "./types/tallySyncReport";
import { TallySyncStatusPayload } from "./types/tallySyncStatus";

interface AdminDashboardProps {
  onLogout: () => void;
}

type AdminSection =
  | "users"
  | "roles"
  | "sync"
  | "checklist"
  | "compliance"
  | "reports";

const ADMIN_SECTIONS: AdminSection[] = [
  "users",
  "roles",
  "sync",
  "checklist",
  "compliance",
  "reports",
];

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const getInitialSection = (): AdminSection => {
    const tab = searchParams.get("tab");
    if (tab === "overview") {
      return "users";
    }
    // Legacy GL tab → Sync (GL sync + mappings live here now)
    if (tab === "gl") {
      return "sync";
    }
    if (tab && ADMIN_SECTIONS.includes(tab as AdminSection)) {
      return tab as AdminSection;
    }
    return "users";
  };

  const [activeSection, setActiveSection] =
    useState<AdminSection>(getInitialSection());

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "overview") {
      navigate(`${location.pathname}?tab=users`, { replace: true });
      return;
    }
    if (tab === "gl") {
      navigate(`${location.pathname}?tab=sync`, { replace: true });
      return;
    }
    if (tab && ADMIN_SECTIONS.includes(tab as AdminSection)) {
      const newSection = tab as AdminSection;
      setActiveSection((prevSection) =>
        newSection !== prevSection ? newSection : prevSection,
      );
    }
  }, [searchParams, navigate, location.pathname]);

  const isTallyBackend = getStoredAccountingBackend() === "tally";
  const [glMappingsRefreshKey, setGlMappingsRefreshKey] = useState(0);
  const [tallySyncStatus, setTallySyncStatus] =
    useState<TallySyncStatusPayload | null>(null);

  const refreshTallySyncStatus = async (): Promise<void> => {
    if (!isTallyBackend) {
      return;
    }
    try {
      const res = await tallySyncApi.getStatus();
      setTallySyncStatus(res.data);
    } catch {
      // Non-blocking — cards still work without last-synced labels.
    }
  };

  useEffect(() => {
    if (activeSection === "sync" && isTallyBackend) {
      void refreshTallySyncStatus();
    }
  }, [activeSection, isTallyBackend]);

  const lastSynced = (module: TallySyncModule): string | null =>
    tallySyncStatus?.modules?.[module] ?? null;

  const [syncingCurrencies, setSyncingCurrencies] = useState(false);
  const [syncingTaxes, setSyncingTaxes] = useState(false);
  const [syncingChartOfAccounts, setSyncingChartOfAccounts] = useState(false);
  const [currencySyncResult, setCurrencySyncResult] =
    useState<SyncResult | null>(null);
  const [taxSyncResult, setTaxSyncResult] = useState<SyncResult | null>(null);
  const [chartOfAccountsSyncResult, setChartOfAccountsSyncResult] =
    useState<ChartOfAccountsSyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const handleSyncCurrencies = async () => {
    setSyncingCurrencies(true);
    setSyncError(null);
    setCurrencySyncResult(null);

    try {
      const result = await syncCurrencies();
      setCurrencySyncResult(result);
    } catch (error) {
      setSyncError(
        error instanceof Error ? error.message : "Failed to sync currencies",
      );
    } finally {
      setSyncingCurrencies(false);
    }
  };

  const handleSyncTaxes = async () => {
    setSyncingTaxes(true);
    setSyncError(null);
    setTaxSyncResult(null);

    try {
      const result: SyncResult = await syncTaxes();
      setTaxSyncResult(result);
    } catch (error) {
      setSyncError(
        error instanceof Error ? error.message : "Failed to sync taxes",
      );
    } finally {
      setSyncingTaxes(false);
    }
  };

  const handleSyncChartOfAccounts = async () => {
    setSyncingChartOfAccounts(true);
    setSyncError(null);
    setChartOfAccountsSyncResult(null);

    try {
      const result = await syncChartOfAccounts();
      setChartOfAccountsSyncResult(result);
      setGlMappingsRefreshKey((k) => k + 1);
    } catch (error) {
      setSyncError(
        error instanceof Error
          ? error.message
          : "Failed to sync chart of accounts",
      );
    } finally {
      setSyncingChartOfAccounts(false);
    }
  };

  const handleTabClick = (section: AdminSection, subTab?: string): void => {
    setActiveSection(section);
    const currentPath = window.location.pathname;
    const url = subTab
      ? `${currentPath}?tab=${section}&subTab=${subTab}`
      : `${currentPath}?tab=${section}`;
    navigate(url, { replace: true });
  };

  const renderSection = (): React.ReactNode => {
    switch (activeSection) {
      case "users":
        return <UserManagementPage />;

      case "roles":
        return <RoleManagementPage />;

      case "checklist":
        return <ChecklistTemplatePage />;

      case "compliance":
        return <ComplianceCalendarPage />;

      case "reports":
        return <DocumentChangeReportsPage />;

      case "sync":
        if (isTallyBackend) {
          return (
            <div className="flex w-full flex-col items-start gap-6 p-8">
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Tally Sync
                </span>
                <span className="text-body font-body text-subtext-color">
                  Sync masters and vouchers from Tally into Zournal, and manage
                  GL bill/invoice flags
                </span>
              </div>

              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Masters
                </span>
                <SyncModuleCard
                  title="GLs & Groups"
                  description="Pull ledgers and groups/sub-groups from Tally into Zournal. Rows missing in Tally will be deleted from Zournal."
                  longRunningHint="Large charts of accounts can take several minutes."
                  lastSyncedAt={lastSynced("gl")}
                  syncLabel="Sync from Tally"
                  onSync={(dryRun) => tallySyncApi.syncGl(dryRun)}
                  onSuccess={() => {
                    setGlMappingsRefreshKey((k) => k + 1);
                    void refreshTallySyncStatus();
                  }}
                />
                <SyncModuleCard
                  title="Customers"
                  description="Pull Sundry Debtor ledgers from Tally. Creates stubs for new parties; deletes Zournal customers missing from Tally."
                  lastSyncedAt={lastSynced("customers")}
                  syncLabel="Sync Customers"
                  onSync={(dryRun) => tallySyncApi.syncCustomers(dryRun)}
                  onSuccess={() => void refreshTallySyncStatus()}
                />
                <SyncModuleCard
                  title="Vendors"
                  description="Pull Sundry Creditor ledgers from Tally. Creates stubs for new parties; deletes Zournal vendors missing from Tally."
                  lastSyncedAt={lastSynced("vendors")}
                  syncLabel="Sync Vendors"
                  onSync={(dryRun) => tallySyncApi.syncVendors(dryRun)}
                  onSuccess={() => void refreshTallySyncStatus()}
                />
              </div>

              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Transactions
                </span>
                <SyncModuleCard
                  title="Invoices"
                  description="Pull Sales vouchers from Tally into Zournal invoices (create, update, and delete when removed in Tally)."
                  lastSyncedAt={lastSynced("invoices")}
                  syncLabel="Sync Invoices"
                  onSync={(dryRun) => tallySyncApi.syncInvoices(dryRun)}
                  onSuccess={() => void refreshTallySyncStatus()}
                />
                <SyncModuleCard
                  title="Bills"
                  description="Pull Purchase vouchers from Tally into Zournal bills (create, update, and delete when removed in Tally)."
                  lastSyncedAt={lastSynced("bills")}
                  syncLabel="Sync Bills"
                  onSync={(dryRun) => tallySyncApi.syncBills(dryRun)}
                  onSuccess={() => void refreshTallySyncStatus()}
                />
                <SyncModuleCard
                  title="Journals"
                  description="Pull Journal vouchers from Tally into Zournal journals (create, update, and delete when removed in Tally)."
                  lastSyncedAt={lastSynced("journals")}
                  syncLabel="Sync Journals"
                  onSync={(dryRun) => tallySyncApi.syncJournals(dryRun)}
                  onSuccess={() => void refreshTallySyncStatus()}
                />
              </div>

              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  GL mappings
                </span>
                <AdminGlMappingsTable key={glMappingsRefreshKey} />
              </div>
            </div>
          );
        }

        return (
          <div className="flex w-full flex-col items-start gap-6 p-8">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Zoho Sync
              </span>
              <span className="text-body font-body text-subtext-color">
                Sync chart of accounts, currencies, and taxes from Zoho Books,
                and manage GL bill/invoice flags
              </span>
            </div>

            {syncError && (
              <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-red-200 bg-red-50 p-4">
                <span className="text-body font-body text-red-600">
                  {syncError}
                </span>
              </div>
            )}

            <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full items-center justify-between">
                <div className="flex flex-col items-start gap-1">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Sync Chart of Accounts
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    Fetch and sync chart of accounts from Zoho Books
                  </span>
                </div>
                <Button
                  variant="brand-primary"
                  icon={<FeatherRefreshCw />}
                  onClick={handleSyncChartOfAccounts}
                  disabled={syncingChartOfAccounts}
                >
                  {syncingChartOfAccounts
                    ? "Syncing..."
                    : "Sync Chart of Accounts"}
                </Button>
              </div>

              {chartOfAccountsSyncResult && (
                <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-green-200 bg-green-50 p-4">
                  <span className="text-body font-body font-semibold text-green-800">
                    Sync Completed Successfully
                  </span>
                  <div className="flex w-full flex-col items-start gap-2 text-sm text-green-700">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Total from Zoho:</span>
                      <span>
                        {chartOfAccountsSyncResult.data.total_from_zoho}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Processed Accounts:</span>
                      <span>
                        {chartOfAccountsSyncResult.data.processed_accounts}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">New Added:</span>
                      <span className="text-green-600">
                        {chartOfAccountsSyncResult.data.new_added}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Updated:</span>
                      <span>{chartOfAccountsSyncResult.data.updated}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Deleted:</span>
                      <span>{chartOfAccountsSyncResult.data.deleted}</span>
                    </div>
                    {chartOfAccountsSyncResult.data.errors > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-red-600">
                          Errors:
                        </span>
                        <span className="text-red-600">
                          {chartOfAccountsSyncResult.data.errors}
                        </span>
                      </div>
                    )}
                    {chartOfAccountsSyncResult.data.error_details &&
                      chartOfAccountsSyncResult.data.error_details.length >
                        0 && (
                        <div className="flex w-full flex-col items-start gap-1 mt-2">
                          <span className="font-semibold text-red-600">
                            Error Details:
                          </span>
                          <ul className="list-disc list-inside text-xs">
                            {chartOfAccountsSyncResult.data.error_details.map(
                              (error: string, index: number) => (
                                <li key={index} className="text-red-600">
                                  {error}
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                  </div>

                  <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-blue-200 bg-blue-50 p-4 mt-1">
                    <span className="text-body font-body font-semibold text-blue-900">
                      Gl&apos;s Auto Assignment Rules
                    </span>
                    {chartOfAccountsSyncHadBillInvoiceRuleFailure(
                      chartOfAccountsSyncResult.data.error_details,
                    ) ? (
                      <span className="text-sm text-amber-800">
                        {GL_SYNC_BILL_INVOICE_RULES_FAILED_MESSAGE}
                      </span>
                    ) : (
                      <>
                        <span className="text-sm text-blue-800">
                          The following rules were applied to GL mappings during
                          sync:
                        </span>
                        <ul className="list-disc list-inside text-sm text-blue-800">
                          {GL_SYNC_BILL_INVOICE_RULES.map((rule) => (
                            <li key={rule.id}>
                              <span className="font-semibold">
                                {rule.flagLabel}
                              </span>
                              {" — "}
                              {rule.description}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full items-center justify-between">
                <div className="flex flex-col items-start gap-1">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Sync Currencies
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    Fetch and sync currencies from Zoho Books
                  </span>
                </div>
                <Button
                  variant="brand-primary"
                  icon={<FeatherRefreshCw />}
                  onClick={handleSyncCurrencies}
                  disabled={syncingCurrencies}
                >
                  {syncingCurrencies ? "Syncing..." : "Sync Currencies"}
                </Button>
              </div>

              {currencySyncResult && (
                <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-green-200 bg-green-50 p-4">
                  <span className="text-body font-body font-semibold text-green-800">
                    Sync Completed Successfully
                  </span>
                  <div className="flex w-full flex-col items-start gap-2 text-sm text-green-700">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Total from Zoho:</span>
                      <span>{currencySyncResult.data.total_from_zoho}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">New Added:</span>
                      <span className="text-green-600">
                        {currencySyncResult.data.new_added}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Existing:</span>
                      <span>{currencySyncResult.data.existing}</span>
                    </div>
                    {currencySyncResult.data.errors > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-red-600">
                          Errors:
                        </span>
                        <span className="text-red-600">
                          {currencySyncResult.data.errors}
                        </span>
                      </div>
                    )}
                    {currencySyncResult.data.error_details.length > 0 && (
                      <div className="flex w-full flex-col items-start gap-1 mt-2">
                        <span className="font-semibold text-red-600">
                          Error Details:
                        </span>
                        <ul className="list-disc list-inside text-xs">
                          {currencySyncResult.data.error_details.map(
                            (error, index) => (
                              <li key={index} className="text-red-600">
                                {error}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full items-center justify-between">
                <div className="flex flex-col items-start gap-1">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Sync Taxes
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    Fetch and sync taxes from Zoho Books
                  </span>
                </div>
                <Button
                  variant="brand-primary"
                  icon={<FeatherRefreshCw />}
                  onClick={handleSyncTaxes}
                  disabled={syncingTaxes}
                >
                  {syncingTaxes ? "Syncing..." : "Sync Taxes"}
                </Button>
              </div>

              {taxSyncResult && (
                <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-green-200 bg-green-50 p-4">
                  <span className="text-body font-body font-semibold text-green-800">
                    Sync Completed Successfully
                  </span>
                  <div className="flex w-full flex-col items-start gap-2 text-sm text-green-700">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Total from Zoho:</span>
                      <span>{taxSyncResult.data.total_from_zoho}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">New Added:</span>
                      <span className="text-green-600">
                        {taxSyncResult.data.new_added}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Existing:</span>
                      <span>{taxSyncResult.data.existing}</span>
                    </div>
                    {taxSyncResult.data.errors > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-red-600">
                          Errors:
                        </span>
                        <span className="text-red-600">
                          {taxSyncResult.data.errors}
                        </span>
                      </div>
                    )}
                    {taxSyncResult.data.error_details.length > 0 && (
                      <div className="flex w-full flex-col items-start gap-1 mt-2">
                        <span className="font-semibold text-red-600">
                          Error Details:
                        </span>
                        <ul className="list-disc list-inside text-xs">
                          {taxSyncResult.data.error_details.map(
                            (error, index) => (
                              <li key={index} className="text-red-600">
                                {error}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                GL mappings
              </span>
              <AdminGlMappingsTable key={glMappingsRefreshKey} />
            </div>

            <TdsSyncCard />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Admin Dashboard
                  </span>
                  <Badge variant="brand">Admin</Badge>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Welcome back, {user?.email}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="brand-secondary"
                icon={<FeatherBriefcase />}
                iconRight={<FeatherArrowRight />}
                onClick={() => navigate("/dashboard/reco")}
              >
                Go to workbench
              </Button>
              <Button
                variant="neutral-tertiary"
                icon={<FeatherLogOut />}
                onClick={onLogout}
              >
                Logout
              </Button>
            </div>
          </div>

          {/*
            Deprecation notice: the tenant Settings area (/settings/*) is the
            replacement for this dashboard. Nothing here is removed — the notice
            only points at the successor. A plain <a target="_blank"> on purpose,
            not navigate(): it opens in a new tab as asked, and cmd/middle-click
            keeps working because no onClick swallows the event.
          */}
          <div className="flex w-full flex-wrap items-start gap-3 rounded-lg border border-solid border-amber-200 bg-amber-50 p-3">
            <FeatherAlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-800" />
            <span className="min-w-0 grow shrink-0 basis-0 text-body font-body text-amber-800">
              This page is deprecated and will be removed — please use the latest
              settings page.
            </span>
            <a
              href="/settings/company-details"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-none items-center gap-1.5 rounded-md border border-solid border-amber-300 bg-white px-3 py-1.5 text-caption-bold font-caption-bold text-amber-800 transition-colors hover:bg-amber-100"
            >
              Open settings
              <FeatherArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>

          <Tabs>
            <Tabs.Item
              active={activeSection === "users"}
              onClick={() => handleTabClick("users")}
            >
              <FeatherUsers className="h-4 w-4" />
              Users
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "roles"}
              onClick={() => handleTabClick("roles")}
            >
              <FeatherShield className="h-4 w-4" />
              Roles
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "checklist"}
              onClick={() => handleTabClick("checklist")}
            >
              <FeatherCheckSquare className="h-4 w-4" />
              Month End Checklist
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "sync"}
              onClick={() => handleTabClick("sync")}
            >
              <FeatherRefreshCw className="h-4 w-4" />
              Sync
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "reports"}
              onClick={() => handleTabClick("reports")}
            >
              <FeatherBarChart2 className="h-4 w-4" />
              Reports
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "compliance"}
              onClick={() => handleTabClick("compliance")}
            >
              <FeatherFileText className="h-4 w-4" />
              Compliance
            </Tabs.Item>
          </Tabs>
        </div>

        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
          {renderSection()}
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default AdminDashboard;
